import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Fade
} from '@mui/material';
import { Refresh, Settings } from '@mui/icons-material';
import ActivityChart from './ActivityChart';
import StatsCards from './StatsCards';
import RecentActivity from './RecentActivity';
import CurrentChallenge from './CurrentChallenge';
import ChallengesList from './ChallengesList';
import FriendsLeaderboard from './FriendsLeaderboard';
import { useDashboard } from '../../hooks/useDashboard';

const Dashboard = () => {
  const {
    stats,
    todayStats,
    activityData,
    recentActivity,
    currentChallenge,
    userChallenges,
    friendsLeaderboard,
    loading,
    error,
    refreshData,
    setError
  } = useDashboard();

  const [chartType, setChartType] = useState('bar');

  if (loading && !stats && !todayStats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading your dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your fitness progress and achievements
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={refreshData} disabled={loading} title="Refresh Dashboard">
            <Refresh />
          </IconButton>
          <IconButton title="Dashboard Settings">
            <Settings />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Fade in={!!error}>
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => setError(null)}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={refreshData}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3} sx={{ width: '100%' }}>
        {/* Row 1: Challenges List */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <ChallengesList
            challenges={userChallenges}
            loading={loading}
            onChallengeClick={(challenge) => window.location.href = `/challenges/${challenge.id || challenge.challenge_id}`}
            onJoinCreateClick={() => window.location.href = '/challenges'}
          />
        </Grid>

        {/* Row 2: Friends Leaderboard */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <FriendsLeaderboard
            leaderboard={friendsLeaderboard}
            loading={loading}
            onChatClick={() => window.location.href = '/friends'}
          />
        </Grid>

        {/* Row 3: Stats Cards */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <StatsCards 
            stats={stats} 
            todayStats={todayStats} 
            loading={loading} 
          />
        </Grid>

        {/* Row 4: Activity Trends Chart */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Activity Trends
                </Typography>
                <Box>
                  {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                </Box>
              </Box>
              <ActivityChart 
                data={activityData} 
                loading={loading}
                chartType={chartType}
                onChartTypeChange={setChartType}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Row 5: Recent Activity */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <RecentActivity 
            data={recentActivity} 
            loading={loading} 
          />
        </Grid>

        {/* Row 6: Quick Actions */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => window.location.href = '/devices'}
                >
                  Sync Devices
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => window.location.href = '/challenges'}
                >
                  View All Challenges
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => window.location.href = '/friends'}
                >
                  Manage Friends
                </Button>
                <Button 
                  variant="outlined"
                  onClick={refreshData}
                  disabled={loading}
                >
                  Refresh Data
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;